import { getScrollableAncestor } from '@/utils/dom/getScrollableAncestor';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  CSSProperties,
  Key,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  useHiddenSafeMeasureElement,
  useIsElementHidden,
} from './hooks/useHiddenSafeMeasurement';

/** Everything a consumer needs to position + measure one virtualized item. */
export type VirtualItemRenderArgs = {
  index: number;
  /** Vertical offset (px) of this item within the list's own box. */
  start: number;
  /** Attach to the item's root element so its real height is measured. */
  measureRef: (node: Element | null) => void;
  key: Key;
};

export type VirtualListProps<T> = {
  items: T[];
  /** Stable key per row — keeps a row's measured height tied to its identity. */
  getItemKey: (index: number) => string | number;
  /**
   * First-paint height guess (px) before each item measures itself. Only
   * affects the initial scrollbar thumb; real heights are measured on mount.
   */
  estimateSize: number;
  /** Extra items rendered above/below the viewport to hide scroll seams. */
  overscan?: number;
  /**
   * Class for the wrapper element. In the default (owned-viewport) mode this
   * is the bounded scroll box and needs a `max-height`; in `windowScroll` mode
   * it is just a positioning wrapper and should NOT set overflow/height.
   */
  viewportClassName?: string;
  /** Class for the inner spacer `<ul>` sized to the full list height. */
  listClassName?: string;
  testId?: string;
  /**
   * Window against the nearest scrollable ancestor (e.g. the drawer body)
   * instead of owning a bounded internal scroll viewport. The list then grows
   * with the page and scrolls with that ancestor — a single scrollbar, like a
   * plain table — while still only mounting the visible window of items.
   *
   * Falls back to the owned viewport if no scrollable ancestor is found, so
   * virtualization degrades gracefully rather than mounting every row.
   */
  windowScroll?: boolean;
  children: (item: T, args: VirtualItemRenderArgs) => ReactNode;
};

const DEFAULT_OVERSCAN = 6;

// `useLayoutEffect` measures before paint (no misaligned-item flash) but warns
// under SSR; fall back to `useEffect` on the server.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Window-only list: renders just the visible slice (+overscan) of `items` so a
 * 2,000+ row list stays cheap to mount. Item heights may vary — each item is
 * measured via `measureRef`, so `estimateSize` only needs to be a ballpark.
 *
 * Two scroll modes:
 * - default: owns a bounded scroll viewport (`viewportClassName` supplies the
 *   `max-height`), same shape as the transaction Edit table's `useVirtualItem`.
 * - `windowScroll`: windows against the nearest scrollable ancestor, so the
 *   list scrolls with the surrounding page (single scrollbar). Because that
 *   ancestor usually has content above the list, the list's offset within it
 *   is measured as `scrollMargin` and the item `start` is normalised back to
 *   the list's own box.
 *
 * The consumer renders each item's root element (kept generic so it can be an
 * `<li>` under the `<ul>` spacer, preserving list semantics). Apply
 * `position: absolute; transform: translateY(start)` to that root.
 */
export const VirtualList = <T,>({
  items,
  getItemKey,
  estimateSize,
  overscan = DEFAULT_OVERSCAN,
  viewportClassName,
  listClassName,
  testId,
  windowScroll = false,
  children,
}: VirtualListProps<T>) => {
  // In owned-viewport mode this div is the scroll element; in windowScroll mode
  // it is only a positioning wrapper used to measure the list's offset.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollAncestorRef = useRef<Element | null>(null);

  const getScrollElement = useCallback((): Element | null => {
    if (!windowScroll) return wrapperRef.current;
    if (!scrollAncestorRef.current) {
      scrollAncestorRef.current = getScrollableAncestor(wrapperRef.current);
    }
    // Fall back to the wrapper (owned viewport) if nothing scrollable is found.
    return scrollAncestorRef.current ?? wrapperRef.current;
  }, [windowScroll]);

  // Offset of the list's top from the top of the scroll element's content.
  // Invariant to scroll position; only changes when content above the list
  // (or the layout) resizes, so we recompute on those.
  const [scrollMargin, setScrollMargin] = useState(0);

  const isWrapperHidden = useIsElementHidden(wrapperRef);

  const measureScrollMargin = useCallback(() => {
    const wrapper = wrapperRef.current;
    const scrollEl = getScrollElement();
    if (!wrapper || !scrollEl || scrollEl === wrapper) return;
    if (isWrapperHidden()) return;
    const next =
      wrapper.getBoundingClientRect().top -
      scrollEl.getBoundingClientRect().top +
      scrollEl.scrollTop;
    // Guard against measurement jitter causing render churn.
    setScrollMargin((prev) => (Math.abs(prev - next) > 1 ? next : prev));
  }, [getScrollElement, isWrapperHidden]);

  useIsomorphicLayoutEffect(() => {
    if (!windowScroll) {
      setScrollMargin(0);
      return;
    }

    measureScrollMargin();

    const scrollEl = getScrollElement();
    const observer = new ResizeObserver(measureScrollMargin);
    if (wrapperRef.current) observer.observe(wrapperRef.current);
    if (scrollEl && scrollEl !== wrapperRef.current) {
      observer.observe(scrollEl);
      // The list's offset shifts whenever a sibling *above* it grows or shrinks
      // (e.g. a card-mode section still measuring its cards, or another section
      // expanding/collapsing). Those siblings are children of the scroll
      // container and resize without touching this list's wrapper or the scroll
      // element's border box, so observing them directly is what keeps
      // `scrollMargin` — and therefore the top items' positions/range — correct
      // without waiting for a drawer resize.
      Array.from(scrollEl.children).forEach((child) => observer.observe(child));
    }
    window.addEventListener('resize', measureScrollMargin);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measureScrollMargin);
    };
  }, [windowScroll, getScrollElement, items.length, measureScrollMargin]);

  const effectiveScrollMargin = windowScroll ? scrollMargin : 0;

  const measureItemElement = useHiddenSafeMeasureElement(estimateSize);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement,
    estimateSize: useCallback(() => estimateSize, [estimateSize]),
    overscan,
    getItemKey,
    scrollMargin: effectiveScrollMargin,
    measureElement: measureItemElement,
  });

  // Safety net for the frame this list appears in (e.g. a layout switch): the
  // virtualizer only recomputes its visible range on a scroll/resize event, so
  // if nothing fires it can hold a stale range and render a blank window until
  // the user resizes the drawer. On the next frame, re-measure and dispatch a
  // synthetic scroll so the virtualizer re-reads the current offset and range.
  // Ongoing shifts are handled deterministically by the ResizeObserver above.
  useIsomorphicLayoutEffect(() => {
    if (!windowScroll) return;
    const raf = requestAnimationFrame(() => {
      if (isWrapperHidden()) return;
      measureScrollMargin();
      const scrollEl = getScrollElement();
      if (scrollEl && scrollEl !== wrapperRef.current) {
        scrollEl.dispatchEvent(new Event('scroll'));
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [
    windowScroll,
    getScrollElement,
    measureScrollMargin,
    isWrapperHidden,
    items.length,
  ]);

  const listStyle: CSSProperties = {
    height: virtualizer.getTotalSize(),
    position: 'relative',
  };

  return (
    <div
      ref={wrapperRef}
      className={windowScroll ? undefined : viewportClassName}
      data-testid={testId}
    >
      <ul className={listClassName} style={listStyle}>
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const item = items[virtualRow.index];
          if (item === undefined) return null;
          return children(item, {
            index: virtualRow.index,
            // `start` is measured from the scroll element's origin, which in
            // windowScroll mode includes `scrollMargin`; normalise it back to
            // the list's own box so `translateY` positions correctly.
            start: virtualRow.start - effectiveScrollMargin,
            measureRef: virtualizer.measureElement,
            key: virtualRow.key,
          });
        })}
      </ul>
    </div>
  );
};
VirtualList.displayName = 'VirtualList';
